// Admin v2 — Statisztikák (Figma ADM_08): játékok, aktív játékosok, átlagos játékidő, aktív helyszínek,
// csúcsidőszakok, játékmódok eloszlása, legutóbbi mérkőzések — a match_history kollekcióból.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, limit, query, Timestamp, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { fmtInt, pctDelta, timeAgo, toDate } from '../data';
import { Avatar, Badge, Card, Empty, IconTile, PageHeader, SectionTitle, Segmented, Spinner, StatCard, Tone } from '../ui';

type Range = 'week' | 'month' | 'year';
interface M { id: string; userId: string; opponent: string; result: string; mode: string; venue: string; at: Date; durSec: number }

const DAYS: Record<Range, number> = { week: 7, month: 30, year: 365 };
const MODE_COLORS = ['#00AFF2', '#a855f7', '#d9a514', '#2bc48a', '#ef4444', '#8a8f99'];

export default function StatisticsPage() {
  const nav = useNavigate();
  const [range, setRange] = useState<Range>('month');
  const [rows, setRows] = useState<M[] | null>(null);
  const [names, setNames] = useState<Record<string, { name: string; avatar: string | null }>>({});
  const [activeVenues, setActiveVenues] = useState<number | null>(null);

  useEffect(() => {
    const from = Timestamp.fromDate(new Date(Date.now() - 2 * 365 * 864e5));
    getDocs(query(collection(db, 'match_history'), where('date', '>=', from), limit(8000)))
      .then((s) => setRows(s.docs.map((d) => {
        const v = d.data();
        return {
          id: d.id, userId: String(v.userId ?? ''), opponent: String(v.opponent ?? ''), result: String(v.result ?? ''),
          mode: String(v.gameMode ?? 'Egyéb'), venue: String(v.venue ?? ''), at: toDate(v.date) ?? new Date(0),
          durSec: Number(v.durationSec ?? v.duration ?? 0),
        };
      }).sort((a, b) => b.at.getTime() - a.at.getTime())))
      .catch(() => setRows([]));
    getDocs(collection(db, 'users')).then((s) => {
      const m: Record<string, { name: string; avatar: string | null }> = {};
      s.docs.forEach((d) => { const v = d.data(); m[d.id] = { name: v.username || v.displayName || 'Játékos', avatar: v.avatarUrl || v.photoURL || null }; });
      setNames(m);
    }).catch(() => undefined);
    Promise.all([
      getDocs(collection(db, 'clubs')).then((s) => s.docs.filter((d) => d.data().verified === true).length).catch(() => 0),
      getDocs(collection(db, 'venues')).then((s) => s.docs.filter((d) => d.data().status !== 'inactive' && d.data().isActive !== false).length).catch(() => 0),
    ]).then(([a, b]) => setActiveVenues(a + b));
  }, []);

  const st = useMemo(() => {
    if (!rows) return null;
    const days = DAYS[range];
    const now = Date.now();
    const cur = rows.filter((r) => r.at.getTime() > now - days * 864e5);
    const prev = rows.filter((r) => r.at.getTime() <= now - days * 864e5 && r.at.getTime() > now - 2 * days * 864e5);
    const players = new Set(cur.map((r) => r.userId)).size;
    const playersPrev = new Set(prev.map((r) => r.userId)).size;
    const withDur = cur.filter((r) => r.durSec > 0);
    const avgMin = withDur.length ? Math.round(withDur.reduce((s, r) => s + r.durSec, 0) / withDur.length / 60) : null;
    const byHour = Array.from({ length: 24 }, (_, h) => cur.filter((r) => r.at.getHours() === h).length);
    const modes: Record<string, number> = {};
    cur.forEach((r) => { const k = /cricket/i.test(r.mode) ? 'Cricket' : /^(301|501|701)/.test(r.mode) ? r.mode.slice(0, 3) : 'Egyéb'; modes[k] = (modes[k] ?? 0) + 1; });
    const modeList = Object.entries(modes).sort((a, b) => b[1] - a[1]);
    return { cur, prev, players, playersPrev, avgMin, byHour, modeList };
  }, [rows, range]);

  if (!st) return <Spinner />;
  const first = st.byHour.findIndex((n) => n > 0);
  const last = 23 - [...st.byHour].reverse().findIndex((n) => n > 0);
  const hours = first < 0 ? [] : Array.from({ length: last - first + 1 }, (_, i) => first + i);
  const maxH = Math.max(1, ...st.byHour);
  const total = st.modeList.reduce((s, [, n]) => s + n, 0) || 1;
  const word = range === 'week' ? 'előző héthez' : range === 'month' ? 'előző hónaphoz' : 'előző évhez';
  const dlt = (c: number, p: number) => { const d = pctDelta(c, p); return d ? { ...d, text: d.text.replace('előző hónaphoz', word) } : null; };

  // donut
  const R = 54, CIRC = 2 * Math.PI * R;
  let acc = 0;
  const RES: Record<string, { t: string; tone: Tone }> = { win: { t: 'Nyert', tone: 'green' }, loss: { t: 'Vesztett', tone: 'red' }, draw: { t: 'Döntetlen', tone: 'gold' } };

  return (
    <div>
      <PageHeader title="Statisztikák" sub="Részletes elemzések és teljesítmény mutatók"
        right={<Segmented value={range} onChange={setRange} options={[{ value: 'week', label: 'Hét' }, { value: 'month', label: 'Hónap' }, { value: 'year', label: 'Év' }]} />} />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon="Target" tone="cyan" label="Összes játék" value={fmtInt(st.cur.length)} delta={dlt(st.cur.length, st.prev.length)} />
        <StatCard icon="Users" tone="green" label="Aktív játékosok" value={fmtInt(st.players)} valueColor="#2bc48a" delta={dlt(st.players, st.playersPrev)} />
        <Card className="!p-[21px] min-h-[122px]">
          <div className="flex items-center gap-2.5"><IconTile icon="Clock" tone="gold" /><span className="text-[13px] font-medium text-[#B8B8C3]">Átlag játékidő</span></div>
          <div className="mt-2.5 text-[28px] leading-[34px] font-bold text-[#d9a514]">{st.avgMin === null ? '—' : `${st.avgMin} perc`}</div>
          <div className="mt-2 text-[12px] text-[#8a8f99]">{st.avgMin === null ? 'A mérkőzések még nem rögzítenek időtartamot' : 'Játékonként'}</div>
        </Card>
        <StatCard icon="MapPin" tone="purple" label="Aktív helyszínek" value={activeVenues === null ? '…' : fmtInt(activeVenues)} valueColor="#a855f7" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-4 mt-6">
        <Card className="!p-5">
          <SectionTitle>Csúcsidőszakok</SectionTitle>
          {hours.length === 0 ? <Empty>Nincs adat ebben az időszakban.</Empty> : (
            <div className="flex items-end gap-2.5 h-[200px]">
              {hours.map((h) => (
                <div key={h} className="flex-1 flex flex-col items-center justify-end h-full gap-2" title={`${st.byHour[h]} játék`}>
                  <div className="w-full rounded-t-md bg-gradient-to-b from-[#00AFF2] to-[#0b6f9a]" style={{ height: `${Math.max(4, (st.byHour[h] / maxH) * 170)}px` }} />
                  <span className="text-[11px] text-[#8a8f99]">{h}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="!p-5">
          <SectionTitle>Játékmódok eloszlása</SectionTitle>
          {st.modeList.length === 0 ? <Empty>Nincs adat.</Empty> : (
            <>
              <svg viewBox="0 0 140 140" className="w-[140px] h-[140px] mx-auto -rotate-90">
                {st.modeList.map(([k, n], i) => {
                  const len = (n / total) * CIRC;
                  const el = <circle key={k} cx="70" cy="70" r={R} fill="none" stroke={MODE_COLORS[i % MODE_COLORS.length]} strokeWidth="16" strokeDasharray={`${len} ${CIRC - len}`} strokeDashoffset={-acc} />;
                  acc += len;
                  return el;
                })}
              </svg>
              <div className="mt-4 flex flex-col gap-2">
                {st.modeList.map(([k, n], i) => (
                  <div key={k} className="flex items-center gap-2 text-[13px]">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: MODE_COLORS[i % MODE_COLORS.length] }} />
                    <span className="flex-1 text-[#B8B8C3]">{k}</span>
                    <span className="font-semibold text-white">{Math.round((n / total) * 100)}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      <h2 className="mt-8 mb-4 text-[15px] font-extrabold italic uppercase text-white">Legutóbbi mérkőzések</h2>
      <div className="bg-[#15191E] border border-[#31383D] rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[800px] text-left">
          <thead>
            <tr className="bg-[#1d232a] text-[12px] uppercase text-[#8a8f99]">
              <th className="pl-5 pr-4 h-[39px] font-semibold">Játékos</th><th className="px-4 font-semibold">Játékmód</th><th className="px-4 font-semibold">Eredmény</th>
              <th className="px-4 font-semibold">Helyszín</th><th className="px-4 font-semibold">Időpont</th><th className="px-4 font-semibold">Műveletek</th>
            </tr>
          </thead>
          <tbody>
            {st.cur.slice(0, 8).map((r) => (
              <tr key={r.id} className="border-t border-[#31383D] h-[56px]">
                <td className="pl-5 pr-4"><div className="flex items-center gap-2.5"><Avatar src={names[r.userId]?.avatar} name={names[r.userId]?.name} size={30} /><span className="text-[14px] font-semibold text-white">{names[r.userId]?.name ?? '…'}</span></div></td>
                <td className="px-4 text-[14px] text-[#B8B8C3]">{r.mode}</td>
                <td className="px-4">{RES[r.result] ? <Badge tone={RES[r.result].tone}>{RES[r.result].t}</Badge> : '—'}</td>
                <td className="px-4 text-[14px] text-[#B8B8C3]">{r.venue || '—'}</td>
                <td className="px-4 text-[14px] text-[#B8B8C3]">{timeAgo(r.at)}</td>
                <td className="px-4"><button type="button" onClick={() => nav(`/users/${r.userId}`)} className="text-[13px] font-semibold text-[#00AFF2] hover:underline">Részletek</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {st.cur.length === 0 && <Empty>Ebben az időszakban nem volt mérkőzés.</Empty>}
      </div>
    </div>
  );
}
